import {
  Box,
  IconButton,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAppNavigate } from '../../Hooks/back/useAppNavigate';
import PropTypes from 'prop-types';
import Icon from '../../Assets/Icon/CustomIcon';
import { useSelector, useDispatch } from 'react-redux';
import { useMemo } from 'react';
import { menuConfig } from '../../Scripts/Home/Home';
import { DrawerActions } from '../../Reducer/DrawerReducer';
import { useBackStack } from '../../Hooks/back/useBackStack';

export default function MainDrawer({ onClose, statusBar, navBar }) {
  const { navigate } = useAppNavigate();
  const dispatch = useDispatch();
  const { menuInfo, userInfo } = useSelector((state) => state.BaseInfo);
  const { workCd } = useSelector((state) => state.workingCheck);
  const { noticeData } = useSelector((state) => state.homeInfo);
  const hasNewNotice = (noticeData || []).some(
    (item) => String(item?.newYn ?? 'N').toUpperCase() === 'Y'
  );

  useBackStack({
    type: 'drawer',
    onBack: () => {
      dispatch(DrawerActions.CloseDrawer());
    },
  });

  const menuStyles = menuConfig.reduce(
    (acc, { ids, icon, iconColor, iconBg, label }) => {
      ids.forEach((id) => {
        acc[id] = { icon, iconColor, iconBg, label };
      });
      return acc;
    },
    {}
  );

  const topMenus = menuInfo
    .filter((m) => ['M_Notice', 'Mypage'].includes(m.menuId))
    .map((m) => ({
      ...m,
      style: menuStyles[m.proId] || menuStyles[m.menuId],
    }))
    .filter((m) => !!m.style);

  const serviceMenus = useMemo(() => {
    let menus = menuInfo.filter(
      (m) => m.seq < 900 && m.seq > 0 && m.menuId !== 'Home'
    );

    if (userInfo.authCd === 'EMPLOYEE') {
      menus = menus.filter((menu) => {
        if (!workCd) {
          return !menu.requiresWorkCd;
        } else {
          return !menu.hideWhenWorking;
        }
      });
    }

    return menus
      .map((m) => ({
        ...m,
        style: menuStyles[m.menuId],
      }))
      .filter((m) => !!m.style)
      .sort((a, b) => a.seq - b.seq);
  }, [menuInfo, userInfo?.authCd, workCd]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F8F9FB',
        pt: `${statusBar}px`,
        pb: `${navBar}px`,
        px: 2.5,
      }}
    >
      <Box
        sx={{
          height: 64,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 48,
              height: 48,
              border: '1px solid #EEEEEE',
              borderRadius: '100%',
              backgroundColor: userInfo?.photo ? 'transparent' : '#F2F2F2',
            }}
          >
            {userInfo?.photo ? (
              <Avatar
                src={userInfo?.photo}
                alt="프로필 이미지"
                sx={{ width: 48, height: 48 }}
              />
            ) : (
              <Icon.User color="#9A9191" />
            )}
          </Box>
          <Typography sx={{ fontSize: 20 }}>
            <span style={{ fontWeight: 700 }}>{userInfo?.userNm}</span> 님
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ width: 26, height: 26 }} />
        </IconButton>
      </Box>

      {/* 상단 고정 메뉴 */}
      <Box>
        <List sx={{ display: 'flex', gap: 2 }}>
          {topMenus.map((menu) => {
            const IconComponent = menu.style.icon;

            return (
              <ListItem key={menu.menuId} disablePadding sx={{ width: '100%' }}>
                <ListItemButton
                  onClick={() => {
                    navigate(`/${menu.menuId}`);
                    onClose();
                  }}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: menu.style.iconBg,
                    borderRadius: '16px',
                    py: 2,
                    px: 1.5,
                    gap: 1,
                    color: '#737373',
                    transition: '0.2s',
                    '&:hover': { backgroundColor: '#E0E0E0' },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent
                      width={44}
                      height={44}
                      color={menu.style.iconColor}
                    />
                    {menu.menuId === 'M_Notice' && hasNewNotice && (
                      <Box
                        component="span"
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -10,
                          minWidth: 28,
                          px: 0.75,
                          py: 0.25,
                          borderRadius: '999px',
                          bgcolor: '#f5f3ff',
                          color: '#7c3aed',
                          fontSize: 9,
                          fontWeight: 700,
                          lineHeight: 1.2,
                          textAlign: 'center',
                          letterSpacing: '0.02em',
                          border: '1px solid #ede9fe',
                          boxShadow: '0 1px 2px rgba(124, 58, 237, 0.12)',
                          pointerEvents: 'none',
                        }}
                      >
                        NEW
                      </Box>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      lineHeight: 1.2,
                      textAlign: 'center',
                    }}
                  >
                    {menu.style.label}
                  </Typography>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* 전체 서비스 메뉴 */}
      <Box sx={{ flex: 1, overflowY: 'auto', mt: 1 }}>
        <Typography
          sx={{ ml: '2.5px', fontSize: 18, fontWeight: 'bold', mb: 1 }}
        >
          전체 서비스
        </Typography>

        <List>
          {serviceMenus.map((menu) => {
            const IconComponent = menu.style.icon;

            return (
              <ListItem key={menu.menuId} disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (menu.menuId === 'M_TRA') {
                      navigate('/Home', { state: { openTraQr: true } });
                    } else if (menu.menuId === 'M_TBM') {
                      navigate('/Home', { state: { openTbmQr: true } });
                    } else if (menu.menuId === 'M_TBMManager') {
                      navigate('/M_TBMSetting');
                    } else {
                      navigate(`/${menu.menuId}`);
                    }
                    onClose();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 1,
                    px: 2,
                    py: 1.2,
                    transition: '0.2s',
                    '&:hover': { backgroundColor: '#E0E0E0' },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      backgroundColor: menu.style.iconBg || '#FFFFFF',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <IconComponent
                      width={32}
                      height={32}
                      color={menu.style.iconColor}
                    />
                  </Box>

                  <ListItemText
                    primary={menu.menuNm}
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider />
    </Box>
  );
}

MainDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
};
